"use client"

import { useState, useRef, useEffect } from "react"
import { createPortal } from "react-dom"
import { Button } from "@/components/ui/button"
import { GetStartedButton } from '@/components/get-started-button'
import { Textarea } from "@/components/ui/textarea"
import { Card, CardContent } from "@/components/ui/card"
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip"
import { createClientSupabaseClient } from "@/supabase/client"
import { Copy, Loader2, Sparkles, ArrowRight, ArrowDown, ChevronDown, X } from "lucide-react"
import { useToast } from "@/hooks/use-toast"
import { useLanguage } from "@/contexts/language-context"
import { useAuth } from "@/contexts/auth-context"
import { LoginModal } from "@/components/auth"
import { ResponseCard } from "@/components/response-card"
import NovelGenService, { type StreamResponse } from "@/lib/novelgen-service"
import { parseResponseContent } from "@/lib/response-parser"
import { UserTemplate } from "@/supabase/types"
import { useCommunicationOptions } from "@/hooks/use-communication-options"
import { DemoSelector } from "@/components/demo-selector"
import { DemoResult } from "@/components/demo-result"
import { type DemoOption } from "@/lib/demo-data"

export function Hero() {
  const [inputContent, setInputContent] = useState("")
  const [description, setDescription] = useState("")
  const [response, setResponse] = useState("")
  const [translation, setTranslation] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false)

  // 新增状态：流式展示相关
  const [rawStreamContent, setRawStreamContent] = useState("")
  const [isStreamComplete, setIsStreamComplete] = useState(false)
  const [showFormattedResponse, setShowFormattedResponse] = useState(false)

  const [activeScenario, setActiveScenario] = useState("none")
  const [activeTarget, setActiveTarget] = useState("none")
  const [activeTone, setActiveTone] = useState("none")
  const [templates, setTemplates] = useState<UserTemplate[]>([])
  const [activeTemplate, setActiveTemplate] = useState("")
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false)
  const [isInputHovered, setIsInputHovered] = useState(false)
  const [isDescriptionHovered, setIsDescriptionHovered] = useState(false)
  // 新增：高级选项折叠
  const [showAdvanced, setShowAdvanced] = useState(false)

  // Demo-related states
  const [selectedDemoOption, setSelectedDemoOption] = useState<DemoOption | null>(null)
  const [showDemoResult, setShowDemoResult] = useState(false)

  const { toast } = useToast()
  const { t, currentLanguage } = useLanguage()
  const { user, isAuthenticated, userSubscription, refreshUserSubscription, savePersistentState, getPersistentState, clearPersistentState } = useAuth()
  const { scenarios, targets, tones } = useCommunicationOptions()
  const [hasEarlyReturnChecked, setHasEarlyReturnChecked] = useState(false)
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false)

  // DropdownMenu hooks moved to top level
  const [menuHeight, setMenuHeight] = useState(0)
  const menuRef = useRef<HTMLDivElement>(null)

  // DropdownButton hooks moved to top level
  const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({})

  const fetchTemplates = () => {
    if (!isAuthenticated) {
      setTemplates([])
      return
    }
    setIsLoadingTemplates(true)
    fetch('/api/templates', { credentials: 'include' })
      .then(res => res.ok ? res.json() : Promise.reject('failed'))
      .then(data => setTemplates(data.templates || []))
      .catch(err => console.error(err))
      .finally(() => setIsLoadingTemplates(false))
  }

  useEffect(() => {
    const handleChange = () => fetchTemplates()
    window.addEventListener('templates-changed', handleChange)
    return () => window.removeEventListener('templates-changed', handleChange)
  }, [isAuthenticated])

  // 在组件加载时恢复状态
  useEffect(() => {
    if (isAuthenticated) {
      const savedState = getPersistentState();
      if (savedState) {
        if (savedState.inputContent) setInputContent(savedState.inputContent);
        if (savedState.description) setDescription(savedState.description);
        if (savedState.activeScenario) setActiveScenario(savedState.activeScenario);
        if (savedState.activeTarget) setActiveTarget(savedState.activeTarget);
        if (savedState.activeTone) setActiveTone(savedState.activeTone);

        // 恢复状态后清除保存的数据
        clearPersistentState();
      }
    }
  }, [isAuthenticated]);

  // 在组件挂载后测量菜单高度
  useEffect(() => {
    if (menuRef.current) {
      setMenuHeight(menuRef.current.scrollHeight)
    }
  }, [openDropdown])



  const templateOptions = templates.map(t => ({ id: t.id, label: t.name }))

  const handleTemplateSelect = (id: string) => {
    setActiveTemplate(id)
    const tpl = templates.find(t => t.id === id)
    if (tpl) {
      // 模板内容显示在description框中，而不是输入框中
      setDescription(tpl.content)
      if (tpl.scenario) setActiveScenario(tpl.scenario)
      if (tpl.target) setActiveTarget(tpl.target)
      if (tpl.tone) setActiveTone(tpl.tone)
    }
  }

  const handleRunDemo = async (option: DemoOption) => {
    // Set the demo option and show result
    setSelectedDemoOption(option)
    setShowDemoResult(true)

    // Set the form values from demo option
    setInputContent(option.inputContent)
    setDescription(option.description)
    setActiveScenario(option.scenario)
    setActiveTarget(option.target)
    setActiveTone(option.tone)

    // Automatically trigger the AI response generation
    await handleSubmitWithDemoData(option)
  }

  const handleSubmitWithDemoData = async (option: DemoOption) => {
    setIsLoading(true)
    setIsStreaming(true)
    setResponse("")
    setTranslation("")

    // Reset streaming states
    setRawStreamContent("")
    setIsStreamComplete(false)
    setShowFormattedResponse(false)

    try {
      const novelgenService = new NovelGenService({})

      const request = {
        content: option.inputContent,
        description: option.description.trim() || undefined,
        scenario: option.scenario,
        target: option.target,
        tone: option.tone,
        userLanguage: currentLanguage,
        contentLanguage: undefined
      }

      // Use streaming response
      const streamGenerator = novelgenService.generateStreamResponse(request)
      let finalResult: StreamResponse | null = null

      for await (const chunk of streamGenerator) {
        setRawStreamContent(chunk.content)

        if (chunk.isComplete) {
          finalResult = chunk
          setIsStreamComplete(true)

          // Set final response and translation
          if (chunk.translation) {
            setResponse(chunk.content.replace(chunk.translation, '').trim())
            setTranslation(chunk.translation)
          } else {
            setResponse(chunk.content)
          }

          // Delay showing formatted response
          setTimeout(() => {
            setShowFormattedResponse(true)
          }, 500)

          break
        }
      }

    } catch (error) {
      console.error("Error generating demo response:", error)
      toast({
        title: t("status.error"),
        description: error instanceof Error ? error.message : t("status.errorDescription"),
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
      setIsStreaming(false)
    }
  }

  const handleSubmit = async () => {
    if (!inputContent.trim()) {
      toast({
        title: t("status.enterContent"),
        description: t("status.enterContentDescription"),
        variant: "destructive",
      })
      return
    }

    // 检查用户是否已登录
    if (!isAuthenticated) {
      // 保存当前状态到localStorage
      savePersistentState({
        inputContent,
        description,
        activeScenario,
        activeTarget,
        activeTone
      });

      // 打开登录模态窗口
      setIsLoginModalOpen(true)
      return
    }

    const supabase = createClientSupabaseClient()

    setIsLoading(true)
    if (userSubscription) {
      const now = Date.now()
      let plan = userSubscription.plan
      let end = userSubscription.current_period_end ? new Date(userSubscription.current_period_end).getTime() : 0
      const endedAt = userSubscription.ended_at ? new Date(userSubscription.ended_at).getTime() : null
      let updateData: any = {}

      if (end <= now) {
        if (!endedAt || endedAt > now) {
          let newEnd = new Date(end)
          do { newEnd.setMonth(newEnd.getMonth() + 1) } while (newEnd.getTime() <= now)
          const qty = plan.startsWith('max') ? 99999 : plan.startsWith('pro') ? 200 : 10
          updateData = { current_period_end: newEnd.toISOString(), quantity: qty }
        } else {
          plan = 'free'
          let newEnd = new Date(end)
          do { newEnd.setMonth(newEnd.getMonth() + 1) } while (newEnd.getTime() <= now)
          updateData = { plan: 'free', current_period_end: newEnd.toISOString(), quantity: 10, ended_at: null }
        }
      }

      if (Object.keys(updateData).length) {
        await supabase.from('user_subscriptions').update(updateData).eq('user_id', user!.id)
        await refreshUserSubscription()
      }

      const qty = updateData.quantity !== undefined ? updateData.quantity : userSubscription.quantity || 0
      if (!plan.startsWith('max') && qty < 1) {
        toast({ title: 'Not enough points', description: 'Insufficient reply points', variant: 'destructive' })
        setIsLoading(false)
        return
      }
    }

    setIsStreaming(true)
    setResponse("")
    setTranslation("")

    // 重置流式展示状态
    setRawStreamContent("")
    setIsStreamComplete(false)
    setShowFormattedResponse(false)

    try {
      // 使用NovelGen服务替代DeepSeek
      const novelgenService = new NovelGenService({})

      const request = {
        content: inputContent,
        description: description.trim() || undefined, // 只在有内容时发送
        scenario: activeScenario,
        target: activeTarget,
        tone: activeTone,
        userLanguage: currentLanguage,
        contentLanguage: undefined // 让服务自动检测
      }

      // 使用流式响应
      const streamGenerator = novelgenService.generateStreamResponse(request)
      let finalResult: StreamResponse | null = null

      for await (const chunk of streamGenerator) {
        setRawStreamContent(chunk.content)

        if (chunk.isComplete) {
          finalResult = chunk
          setIsStreamComplete(true)

          // 设置最终响应和翻译
          if (chunk.translation) {
            setResponse(chunk.content.replace(chunk.translation, '').trim())
            setTranslation(chunk.translation)
          } else {
            setResponse(chunk.content)
          }

          // 延迟显示格式化响应
          setTimeout(() => {
            setShowFormattedResponse(true)
          }, 500)

          break
        }
      }

      // 保存历史记录（异步，不阻塞UI）
      if (finalResult) {
        // 立即保存历史记录，使用cookie认证
        (async () => {
          try {
            // console.log('🔄 开始保存历史记录...')
            // console.log('🔑 使用cookie认证，跳过session获取...')

            const historyData = {
              input_content: inputContent,
              description: description.trim() || null, // 保存description，空字符串转为null
              scenario: activeScenario,
              target: activeTarget,
              tone: activeTone,
              response_content: finalResult.originalResponse || finalResult.content, // 保存原始响应内容
              public: false // 默认设置为false，不公开
            }

            /* console.log('📝 历史记录数据:', {
              input_content_length: historyData.input_content?.length || 0,
              scenario: historyData.scenario,
              target: historyData.target,
              tone: historyData.tone,
              response_content_length: historyData.response_content?.length || 0,
            })*/

            // console.log('🌐 发送POST请求到 /api/history-cookie...')
            const response = await fetch('/api/history-cookie', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              credentials: 'include', // 确保包含cookies
              body: JSON.stringify(historyData)
            });

            // console.log('📡 请求发送完成，状态:', response.status)

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}))
              console.error('❌ Failed to save history:', response.status, errorData)
            } else {
              const responseData = await response.json()
              if (responseData.subscription) {
                await refreshUserSubscription()
              }
            }
          } catch (error) {
            console.error('💥 Error saving history:', error)
            console.error('Error details:', {
              name: error instanceof Error ? error.name : 'Unknown',
              message: error instanceof Error ? error.message : 'Unknown error',
              stack: error instanceof Error ? error.stack : 'No stack trace'
            })
          }
        })()
      }

    } catch (error) {
      console.error("Error generating response:", error)
      toast({
        title: t("status.error"),
        description: error instanceof Error ? error.message : t("status.errorDescription"),
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
      setIsStreaming(false)
    }
  }

  const getSelectedScenario = () => scenarios.find(s => s.id === activeScenario)
  const getSelectedTarget = () => targets.find(t => t.id === activeTarget)
  const getSelectedTone = () => tones.find(t => t.id === activeTone)

  const handleDropdownToggle = (dropdown: string, buttonElement: HTMLButtonElement) => {
    if (openDropdown === dropdown) {
      setOpenDropdown(null)
    } else {
      if (dropdown === 'template') {
        fetchTemplates()
      }
      const rect = buttonElement.getBoundingClientRect()
      // 获取窗口尺寸
      const windowWidth = window.innerWidth
      const windowHeight = window.innerHeight

      // 计算下拉菜单的理想位置
      let top = rect.top - 8 // 8px margin above button
      const left = Math.max(8, rect.left) // 确保至少有8px的左边距
      const width = Math.min(rect.width, windowWidth - 16) // 确保宽度不超过屏幕宽度减去边距

      // 针对手机屏幕做额外处理
      const isMobileScreen = windowWidth <= 640 // sm断点

      // 在移动设备上对不同类型的下拉菜单进行特殊处理
      if (isMobileScreen) {
        // 对于Tone这样的多选项菜单，直接放在屏幕中央位置更合适
        if (dropdown === "tone") {
          top = Math.min(windowHeight * 0.25, rect.top) // 在屏幕顶部25%位置或按钮位置，取较小值
        } else {
          top = rect.bottom + 8 // 其他菜单在按钮下方8px处显示
        }
      }

      setDropdownPosition({
        top,
        left,
        width
      })
      setOpenDropdown(dropdown)
    }
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenDropdown(null)
    }

    if (openDropdown) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [openDropdown])

  const DropdownButton = ({
    type,
    label,
    options,
    activeValue,
    onSelect
  }: {
    type: string
    label: string
    options: Array<{ id: string; label: string }>
    activeValue: string
    onSelect: (value: string) => void
  }) => {
    const isOpen = openDropdown === type
    const selectedOption = options.find(opt => opt.id === activeValue)
    const isMobile = typeof window !== "undefined" && window.innerWidth <= 640

    return (
      <div className="relative">
        <button
          ref={(el) => { buttonRefs.current[type] = el }}
          onClick={(e) => {
            e.stopPropagation()
            const buttonElement = buttonRefs.current[type]
            if (buttonElement) {
              handleDropdownToggle(type, buttonElement)
            }
          }}
          className="flex items-center justify-between gap-2 px-3 py-2.5 w-full sm:w-auto bg-white dark:bg-zinc-800/50 hover:bg-gray-50 dark:hover:bg-zinc-700/50 transition-all duration-200 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-lg min-h-[42px]"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <span className="truncate max-w-[140px] sm:max-w-none">{selectedOption?.label || label}</span>
          <ChevronDown className={`min-w-[14px] w-3.5 h-3.5 text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>
    )
  }

  // Portal dropdown menu
  const DropdownMenu = ({
    type,
    options,
    activeValue,
    onSelect,
    loading,
    emptyLabel
  }: {
    type: string
    options: Array<{ id: string; label: string }>
    activeValue: string
    onSelect: (value: string) => void
    loading?: boolean
    emptyLabel?: string
  }) => {
    if (openDropdown !== type) return null

    // 计算列数
    const getColumnCount = () => {
      // 在移动设备上默认使用单列以节省空间
      if (window.innerWidth <= 640) {
        return 1
      }

      if (options.length <= 8) return 1
      if (options.length <= 16) return 2
      return 3
    }

    const columnCount = getColumnCount()
    // 在移动设备上使菜单更宽以提高可点击性
    const isMobile = window.innerWidth <= 640
    const mobileMenuWidth = Math.min(window.innerWidth - 32, 300) // 最大300px或屏幕宽度减去边距
    const menuWidth = isMobile ? mobileMenuWidth : (columnCount === 1 ? 160 : columnCount === 2 ? 320 : 480)

    // 获取每种下拉菜单类型的优先位置系数（值越小越靠上）
    const getPositionPriority = () => {
      if (isMobile) {
        // 在移动设备上不同类型的下拉菜单有不同的位置优先级
        switch (type) {
          case "scenario": return 0.5 // 场景菜单放中上位置
          case "target": return 0.4 // 目标菜单放中间位置
          case "tone": return 0.3 // 语气菜单放中间偏上位置
          default: return 0.5
        }
      }
      return 1 // 非移动设备返回1，表示使用默认位置
    }

    // 计算最终位置，确保菜单不会超出屏幕
    const calculatePosition = () => {
      const windowHeight = window.innerHeight
      const windowWidth = window.innerWidth

      // 初始位置
      let top = dropdownPosition.top
      let left = dropdownPosition.left

      // 如果是移动设备，将下拉菜单水平居中显示
      if (isMobile) {
        left = Math.max(16, (windowWidth - menuWidth) / 2)

        // 基于菜单类型设置优先位置
        const positionPriority = getPositionPriority()
        // 计算基于屏幕高度和优先级的最佳位置
        const optimalPosition = windowHeight * positionPriority
        // 如果当前位置太低，则使用最佳位置
        if (top > optimalPosition) {
          top = optimalPosition
        }
      }

      // 检查底部边界
      const bottomOverflow = top + menuHeight - windowHeight + 60 // 增加底部安全距离
      if (bottomOverflow > 0) {
        // 如果超出底部，将菜单向上移动
        top = Math.max(16, top - bottomOverflow)
      }

      // 检查顶部边界
      if (top < 16) {
        top = 16 // 至少保留16px的上边距
      }

      return { top, left }
    }

    const { top, left } = calculatePosition()

    return createPortal(
      <div
        ref={menuRef}
        className="fixed bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-700 rounded-lg shadow-xl max-h-[70vh] overflow-y-auto overscroll-contain animate-in fade-in slide-in-from-top-5 duration-200"
        style={{
          top: top,
          left: left,
          width: menuWidth,
          zIndex: 9999,
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(156, 163, 175, 0.5) transparent'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="grid gap-0"
          style={{
            gridTemplateColumns: `repeat(${columnCount}, 1fr)`
          }}
        >
          {loading ? (
            <div className="px-4 py-3 text-center text-sm text-gray-500">{t('common.loading')}</div>
          ) : options.length === 0 ? (
            <button disabled className="px-3 py-3 text-left text-gray-400 text-sm cursor-not-allowed">{emptyLabel || t('common.noItems')}</button>
          ) : (
            options.map((option) => (
              <button
                key={option.id}
                onClick={() => {
                  onSelect(option.id)
                  setOpenDropdown(null)
                }}
                className={`flex items-center gap-2 px-3 py-3 text-left hover:bg-gray-50 dark:hover:bg-zinc-800/70 transition-colors duration-200 text-sm ${activeValue === option.id ? 'bg-blue-50 dark:bg-indigo-950/50 text-blue-700 dark:text-indigo-300' : 'text-gray-700 dark:text-gray-100'
                  }`}
              >
                <div className={`w-1.5 h-1.5 rounded-full ${activeValue === option.id ? 'bg-blue-500 dark:bg-indigo-400' : 'bg-gray-300 dark:bg-zinc-500'
                  }`}></div>
                <span>{option.label}</span>
              </button>
            ))
          )}
        </div>
      </div>,
      document.body
    )
  }

  // 清空文本框的函数
  const clearInputContent = () => {
    setInputContent("")
  }

  // 未登录时显示极简 Hero（大标题 + 副标题 + 登录按钮 + 提示钩子文字）
  if (!isAuthenticated) {
    return (
      <section className="relative min-h-[80vh] flex items-center justify-center py-24 bg-gradient-to-b from-blue-50/70 via-white to-white dark:from-zinc-900 dark:via-zinc-900 dark:to-zinc-950 overflow-hidden">
        {/* Decorative subtle blobs (kept minimal) */}
        <div className="pointer-events-none absolute -top-32 -left-32 h-96 w-96 rounded-full bg-blue-200/30 blur-3xl dark:bg-indigo-700/30" />
        <div className="pointer-events-none absolute bottom-0 right-0 h-80 w-80 rounded-full bg-indigo-300/30 blur-3xl dark:bg-blue-800/30" />
        <div className="container relative z-10 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-8 text-readable">
              {t("main.title")}
            </h1>
            <p className="text-xl md:text-2xl text-muted-readable mb-12 max-w-3xl mx-auto">
              {t("hero.subtitle")}
            </p>
            <GetStartedButton className="h-14 px-10 text-lg bg-blue-600 hover:bg-blue-700 dark:bg-indigo-600 dark:hover:bg-indigo-700 shadow-lg" />
            <div className="mt-12 text-sm md:text-base text-gray-500 dark:text-gray-400 flex items-center justify-center gap-2">
              <span className="inline-flex text-yellow-400">★★★★★</span>
              <span>{t("hero.hook") || "Join thousands of users improving their communication every day."}</span>
            </div>
          </div>
        </div>
      </section>
    )
  }

  return (
    <>
      <section className="relative min-h-screen flex items-center justify-center py-20 bg-gradient-to-b from-blue-50/70 via-white to-white dark:from-zinc-900 dark:via-zinc-900 dark:to-zinc-950 overflow-hidden">
        {/* Decorative blobs */}
        <div className="pointer-events-none absolute -top-24 -left-24 w-72 h-72 bg-blue-200/30 dark:bg-indigo-600/30 rounded-full blur-3xl" />
        <div className="pointer-events-none absolute bottom-10 right-0 w-80 h-80 bg-purple-300/20 dark:bg-indigo-800/30 rounded-full blur-3xl" />
        <div className="container relative z-10 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16 animate-fade-in">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-indigo-950/50 rounded-full border border-blue-200 dark:border-indigo-800/50 mb-8">
                <Sparkles className="w-4 h-4 text-blue-600 dark:text-indigo-400" />
                <span className="text-sm font-medium text-blue-700 dark:text-indigo-300">{t("hero.title")}</span>
              </div>
              <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-8 text-readable">{t("main.title")}</h1>
              <p className="text-xl md:text-2xl text-muted-readable mb-12 max-w-4xl mx-auto">{t("hero.subtitle")}</p>
            </div>
            <div className="grid lg:grid-cols-2 gap-12 items-start max-w-6xl mx-auto">
              <div className="space-y-6 animate-slide-up">
                {!isAuthenticated ? (
                  <DemoSelector onRunDemo={handleRunDemo} isLoading={isLoading} />
                ) : (
                   <Card className="hover-glow-card glass dark:glass-dark shadow-xl border-0 bg-white/95 dark:bg-zinc-900/95">
                    <CardContent className="p-8">
                      <div className="space-y-5">
                        <div className="relative" onMouseEnter={() => setIsInputHovered(true)} onMouseLeave={() => setIsInputHovered(false)}>
                          <Textarea
                            placeholder={t("placeholder.message")}
                            value={inputContent}
                            onChange={(e) => setInputContent(e.target.value)}
                            className="min-h-[180px] resize-none bg-white dark:bg-zinc-800/50 border-slate-200 dark:border-zinc-700 focus:border-blue-500 dark:focus:border-indigo-400 transition-all duration-200 text-base text-readable placeholder:text-gray-500 dark:placeholder:text-gray-400 rounded-xl overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 hover:scrollbar-thumb-gray-400 dark:hover:scrollbar-thumb-gray-500"
                          />
              {inputContent && isInputHovered && (
                            <button
                              onClick={clearInputContent}
                              className="absolute top-3 right-3 p-1.5 bg-gray-100 hover:bg-red-100 dark:bg-zinc-700 dark:hover:bg-red-900/50 text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400 rounded-full transition-all duration-200 hover:scale-110 shadow-sm hover:shadow-md"
                title={t('interface.clearInput')}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        <div>
                          <button
                            type="button"
                            onClick={() => setShowAdvanced(v => !v)}
                            className="w-full flex items-center justify-between gap-2 px-4 py-2 text-sm rounded-lg bg-gray-50 dark:bg-zinc-800/60 hover:bg-gray-100 dark:hover:bg-zinc-700/60 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-zinc-700 transition"
                            aria-expanded={showAdvanced}
                          >
                            <span className="font-medium">{t('interface.advancedOptions') || 'Advanced Options'}</span>
                            <ChevronDown className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
                          </button>
                          <div className={`overflow-hidden transition-[max-height,opacity] duration-500 ease-in-out ${showAdvanced ? 'max-h-[1200px] opacity-100 mt-4' : 'max-h-0 opacity-0'}`}>
                            <div className="flex flex-col xs:flex-row flex-wrap gap-2 mb-4">
                              <div className="w-full xs:w-auto"><DropdownButton type="scenario" label={t("dropdown.scenario")} options={scenarios} activeValue={activeScenario} onSelect={setActiveScenario} /></div>
                              <div className="w-full xs:w-auto"><DropdownButton type="target" label={t("dropdown.target")} options={targets} activeValue={activeTarget} onSelect={setActiveTarget} /></div>
                              <div className="w-full xs:w-auto"><DropdownButton type="tone" label={t("dropdown.tone")} options={tones} activeValue={activeTone} onSelect={setActiveTone} /></div>
                              <div className="w-full xs:w-auto"><DropdownButton type="template" label={t("dropdown.useTemplates")} options={templateOptions} activeValue={activeTemplate} onSelect={handleTemplateSelect} /></div>
                            </div>
                            <div className="relative" onMouseEnter={() => setIsDescriptionHovered(true)} onMouseLeave={() => setIsDescriptionHovered(false)}>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                {!(isAuthenticated && userSubscription?.plan !== 'free') ? t("restriction.descriptionLabel") : t("restriction.descriptionOptional")}
                              </label>
                              <TooltipProvider delayDuration={0}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div>
                                      <Textarea
                                        placeholder={t("restriction.desPrompt")}
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        disabled={!(isAuthenticated && userSubscription?.plan !== 'free')}
                                        className="min-h-[100px] resize-none bg-white dark:bg-zinc-800/50 border-slate-200 dark:border-zinc-700 focus:border-blue-500 dark:focus:border-indigo-400 transition-all duration-200 text-sm text-readable placeholder:text-gray-500 dark:placeholder:text-gray-400 rounded-xl overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 hover:scrollbar-thumb-gray-400 dark:hover:scrollbar-thumb-gray-500"
                                      />
                                    </div>
                                  </TooltipTrigger>
                                </Tooltip>
                              </TooltipProvider>
                              {description && isDescriptionHovered && (
                                <button onClick={() => setDescription("")} className="absolute top-8 right-3 p-1.5 bg-gray-100 hover:bg-red-100 dark:bg-zinc-700 dark:hover:bg-red-900/50 text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400 rounded-full transition-all duration-200 hover:scale-110 shadow-sm hover:shadow-md" title={t('interface.clearDescription')}>
                                  <X className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                        <Button onClick={handleSubmit} disabled={isLoading} className="w-full professional-button bg-blue-600 hover:bg-blue-700 dark:bg-indigo-600 dark:hover:bg-indigo-700 text-white font-semibold py-4 text-base shadow-lg rounded-xl" size="lg">
                          {isLoading ? (
                            <>
                              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                              {t("interface.generating")}
                            </>
                          ) : (
                            <>
                              <Sparkles className="mr-2 h-5 w-5" />
                              {t("interface.generate")}
                              <ArrowRight className="ml-2 h-5 w-5 hidden lg:block" />
                              <ArrowDown className="ml-2 h-5 w-5 block lg:hidden" />
                            </>
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
                {!isAuthenticated && showDemoResult && selectedDemoOption && (<DemoResult option={selectedDemoOption} />)}
              </div>
              <div className="space-y-6 animate-slide-up delay-200">
                {(rawStreamContent || response) ? (
                  <>
                    {!showFormattedResponse && rawStreamContent && (
                      <Card className="glass dark:glass-dark shadow-xl border-0 bg-white/95 dark:bg-zinc-900/95">
                        <CardContent className="p-8">
                          <div className="space-y-4">
                            <div className="flex items-center gap-2 mb-4">
                              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                              <span className="text-sm text-gray-600 dark:text-gray-400">{isStreamComplete ? t("interface.processing") : t("interface.generating")}</span>
                            </div>
                            <div className="bg-gray-50 dark:bg-zinc-800/50 rounded-lg p-4 min-h-[200px]">
                              <pre className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300 font-mono leading-relaxed">
                                {rawStreamContent}
                                {!isStreamComplete && (<span className="inline-block w-2 h-4 bg-blue-500 ml-1 animate-pulse"></span>)}
                              </pre>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                    {showFormattedResponse && response && (
                      <ResponseCard response={response} translation={translation} isStreaming={false} />
                    )}
                  </>
                ) : (
                  <Card className="glass dark:glass-dark shadow-xl border-0 border-dashed border-slate-300 dark:border-zinc-600 bg-white/95 dark:bg-zinc-900/95">
                    <CardContent className="p-12 text-center">
                      <div className="w-16 h-16 mx-auto mb-6 bg-blue-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center">
                        <Sparkles className="w-8 h-8 text-blue-600 dark:text-indigo-400" />
                      </div>
                      <h3 className="text-xl font-semibold mb-3 text-readable">{t("interface.ready")}</h3>
                      <div className="flex items-center justify-center gap-2 text-sm text-muted-readable">
                        <span>{t("interface.fast")}</span><span>•</span><span>{t("interface.accurate")}</span><span>•</span><span>{t("interface.professional")}</span>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
  <DropdownMenu type="template" options={templates.map(t => ({ id: t.id, label: t.name }))} activeValue={activeTemplate} onSelect={handleTemplateSelect} loading={isLoadingTemplates} emptyLabel={t('templates.none')} />
      <DropdownMenu type="scenario" options={scenarios} activeValue={activeScenario} onSelect={setActiveScenario} />
      <DropdownMenu type="target" options={targets} activeValue={activeTarget} onSelect={setActiveTarget} />
      <DropdownMenu type="tone" options={tones} activeValue={activeTone} onSelect={setActiveTone} />
      <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />
    </>
  )
}
